import { Box, HStack, Menu, MenuButton, MenuList, Text } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { Button, Icon, MenuItemRow, TextField } from '../../../ui';
import {
    deliveryDaysLabel,
    PurchaseOrderPart,
    PurchaseOrderPartStatus,
} from '../../../types/inventory/purchaseOrders';

export type PurchaseOrderPartAction = 'confirm' | 'reject';

function ActionLabel({
    status,
}: {
    status: PurchaseOrderPartStatus | PurchaseOrderPartAction | 'pending';
}) {
    if (status === 'confirm' || status === 'confirmed') {
        return (
            <HStack spacing={1} color="success.fg">
                <Icon name="check" size="sm" />
                <Text textStyle="labelSm">Confirm</Text>
            </HStack>
        );
    }
    if (status === 'reject' || status === 'rejected') {
        return (
            <HStack spacing={1} color="error.fg">
                <Icon name="x" size="sm" />
                <Text textStyle="labelSm">Reject</Text>
            </HStack>
        );
    }
    return (
        <Text textStyle="labelSm" color="fg.muted">
            Select…
        </Text>
    );
}

interface PartActionCellProps {
    part: PurchaseOrderPart;
    dirty: PurchaseOrderPartAction | undefined;
    onChange: (action: PurchaseOrderPartAction | undefined) => void;
}

export const PartActionCell = ({ part, dirty, onChange }: PartActionCellProps) => {
    if (part.status !== 'pending') {
        return <ActionLabel status={part.status} />;
    }

    return (
        <Menu>
            <MenuButton
                as={Button}
                size="sm"
                variant="ghostGray"
                // Match static ActionLabel start: no left pad (sm Button default
                // would shift the checkmark/label right of submitted rows).
                pl={0}
                pr={1}
                h="auto"
                minH={0}
                minW={0}
                textStyle="labelSm"
                justifyContent="flex-start"
                rightIcon={<Icon name="chevronDown" size="sm" color="fg.muted" />}
            >
                <ActionLabel status={dirty ?? 'pending'} />
            </MenuButton>
            <MenuList minW="160px" p="6px">
                <MenuItemRow kind="single" isSelected={!dirty} onClick={() => onChange(undefined)}>
                    Select…
                </MenuItemRow>
                <MenuItemRow
                    kind="single"
                    isSelected={dirty === 'confirm'}
                    icon={<Icon name="check" size="sm" color="success.fg" />}
                    onClick={() => onChange('confirm')}
                >
                    Confirm
                </MenuItemRow>
                <MenuItemRow
                    kind="single"
                    isSelected={dirty === 'reject'}
                    icon={<Icon name="x" size="sm" color="error.fg" />}
                    onClick={() => onChange('reject')}
                >
                    Reject
                </MenuItemRow>
            </MenuList>
        </Menu>
    );
};

type EtaPreset = 'same' | 'next' | 'custom';

function presetFromDays(days: number): EtaPreset {
    if (days === 0) return 'same';
    if (days === 1) return 'next';
    return 'custom';
}

interface EtaCellProps {
    value: number;
    editable: boolean;
    onChange: (days: number) => void;
}

export const EtaCell = ({ value, editable, onChange }: EtaCellProps) => {
    const preset = presetFromDays(value);
    const [menuOpen, setMenuOpen] = useState(false);
    const [customDraft, setCustomDraft] = useState(
        preset === 'custom' ? String(value) : '2',
    );
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (preset === 'custom') setCustomDraft(String(value));
    }, [value, preset]);

    useEffect(() => {
        if (!(menuOpen && preset === 'custom')) return;
        const t = setTimeout(() => inputRef.current?.focus(), 0);
        return () => clearTimeout(t);
    }, [menuOpen, preset]);

    if (!editable) {
        return <Text>{deliveryDaysLabel(value)}</Text>;
    }

    const commitCustom = (raw: string) => {
        const digits = raw.replace(/\D/g, '');
        const parsed = parseInt(digits, 10);
        if (!Number.isFinite(parsed) || parsed < 2) {
            onChange(2);
            setCustomDraft('2');
            return;
        }
        onChange(parsed);
        setCustomDraft(String(parsed));
    };

    return (
        <Menu
            isOpen={menuOpen}
            onOpen={() => setMenuOpen(true)}
            onClose={() => {
                if (preset === 'custom') {
                    commitCustom(customDraft);
                }
                setMenuOpen(false);
            }}
            closeOnSelect={false}
        >
            <MenuButton
                as={Button}
                size="xs"
                variant="ghostGray"
                px={1}
                h="auto"
                minW={0}
                fontWeight="medium"
                color="fg.default"
                rightIcon={<Icon name="chevronDown" size="xs" color="fg.muted" />}
            >
                {deliveryDaysLabel(value)}
            </MenuButton>
            <MenuList minW="180px" p="6px">
                <MenuItemRow
                    kind="single"
                    closeOnSelect={false}
                    isSelected={preset === 'same'}
                    onClick={() => {
                        onChange(0);
                        setMenuOpen(false);
                    }}
                >
                    Same Day
                </MenuItemRow>
                <MenuItemRow
                    kind="single"
                    closeOnSelect={false}
                    isSelected={preset === 'next'}
                    onClick={() => {
                        onChange(1);
                        setMenuOpen(false);
                    }}
                >
                    Next Day
                </MenuItemRow>
                <MenuItemRow
                    kind="single"
                    closeOnSelect={false}
                    isSelected={preset === 'custom'}
                    onClick={() => {
                        const next = value >= 2 ? value : 2;
                        onChange(next);
                        setCustomDraft(String(next));
                    }}
                >
                    Custom
                </MenuItemRow>
                {preset === 'custom' ? (
                    <Box px="10px" pt={2} pb={1}>
                        <TextField
                            ref={inputRef}
                            label="Days"
                            type="number"
                            size="sm"
                            value={customDraft}
                            onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '');
                                setCustomDraft(digits);
                            }}
                            onBlur={() => commitCustom(customDraft)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    commitCustom(customDraft);
                                    setMenuOpen(false);
                                }
                                e.stopPropagation();
                            }}
                            onClick={(e) => e.stopPropagation()}
                            inputMode="numeric"
                            min={2}
                            placeholder="2"
                        />
                    </Box>
                ) : null}
            </MenuList>
        </Menu>
    );
};
